import React, { useEffect, useRef, useState } from 'react';

// Components
import SelectedDataPanel from '@/views/components/data/DataTableV2/SelectedDataPanel';
import { LoadingSpinner } from '@/views/components/common';
import { SecondaryLink } from '@/views/components/buttons';

// Libraries
import classNames from 'classnames';
import PerfectScrollbar from 'react-perfect-scrollbar';
import {
  Cell,
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  Header,
  HeaderGroup,
  Row,
  SortingState,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import { isEqual, get, uniqueId } from 'lodash';
import { Waypoint } from 'react-waypoint';

// Types
import Nullable from '@/types/nullable';
import { InfiniteScrollOptions, TableProps } from '@/types/data-table';
import { FEATURE_FLAGS } from '@/enums/FeatureFlags';

// Utils
import Pagination from '@/views/components/pagination/Pagination';
import PaginationPageSizeDropdown, { MINIMUM_PAGE_SIZE } from '@/views/components/pagination/PaginationPageSizeDropdown';
import { ENTER_KEY } from '@/utils/constants';
import { formatMessage, FormattedMessage } from '@/utils/i18n';
import { generateColumnDef } from '@/utils/data-table';
import InvisibleButton from '../../buttons/InvisibleButton';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';

/**
 * Renders a data table component using Tanstack datatable (tst) with various features such as column pinning, sorting, selection, and infinite scrolling.
 *
 * @template T - The type of the data object in the table.
 * @param {TableProps<T>} props - The props for the DataTableV2 component.
 * @returns {JSX.Element} - The rendered DataTableV2 component.
 */
function DataTableV2<T extends object>({
  columnPinningState = {},
  columns,
  columnVisibilityState = {},
  data,
  enableColumnFreeze = false,
  enableMultiselect = false,
  enableSorting = false,
  enableTableSelection = false,
  enablePagination = false,
  hidePaginationTotal = false,
  tableTotalText,
  tableBottomAction,
  getAdditionalRowClassNames,
  infiniteScrollComponent: InfiniteScrollComponent = DefaultInfiniteScrollComponent,
  infiniteScrollOptions,
  initialSort,
  isExpandable = false,
  menuItems,
  multiselectActionsOptions,
  onRowClick,
  perfectScrollBarOptions = {
    suppressScrollX: false,
    suppressScrollY: false,
  },
  noDataComponent: NoDataComponent = DefaultNoDataComponent,
  renderExpandedContent,
  rowSelectionDisabled,
  rowForcefullySelected,
  canRowExpand = () => true,
  serverSideSorting = false,
  sortedColumns,
  tableClassNames = {},
  onClearSelection,
  onServerSideSorting,
  onRowSelectionChange,
  disabledActions,
  getCheckboxTooltip,
  getRowAriaLabelledBy,
  tableAriaLabel,
  tableTestId,
  tableRef,
  getSubRows,
}: TableProps<T>) {
  // Variables Declaration
  const psbRef = useRef<PerfectScrollbar | null>(null);
  const tableUniqueId = useRef(uniqueId('data-table-v2-')).current;

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(columnVisibilityState);
  const [expanded, setExpanded] = React.useState<ExpandedState>({});
  const [isPsbScrolledX, setIsPsbScrolledX] = useState(false);
  const [isPsbScrolledY, setIsPsbScrolledY] = useState(false);
  const [hasHorizontalScroll, setHasHorizontalScroll] = useState(false);
  const [rowSelection, setRowSelection] = React.useState({});
  const [sorting, setSorting] = useState<SortingState>(initialSort ? [initialSort] : []);
  const firstErrorRowRef = useRef<HTMLTableRowElement | null>(null);
  const isClientSideSorting = enableSorting && !serverSideSorting;
  const { isFFEnabled } = useFeatureFlag();
  const isSubRowsDataTableV2Enabled = isFFEnabled(FEATURE_FLAGS.SUB_ROWS_DATATABLEV2_ENABLED);

  // Set the Column Visibility state after initial render
  useEffect(() => {
    if (!isEqual(columnVisibility, columnVisibilityState)) {
      setColumnVisibility(columnVisibilityState);
    }
  }, [columnVisibility, columnVisibilityState]);

  useEffect(() => {
    if (firstErrorRowRef.current) {
      firstErrorRowRef.current.focus();
    }
  }, [data, columnVisibility, rowSelection]);

  // Logic to set the scroll state of the table
  useEffect(() => {
    const htmlTableElement = (psbRef.current as any)?._container as HTMLElement;

    if (htmlTableElement) {
      const handleScroll = () => {
        if (psbRef.current) {
          setIsPsbScrolledX(htmlTableElement.scrollLeft > 0);
          setIsPsbScrolledY(htmlTableElement.scrollTop > 0);
          setHasHorizontalScroll(htmlTableElement.scrollWidth > htmlTableElement.clientWidth);
        }
      };

      // Initial check
      handleScroll();

      htmlTableElement?.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleScroll);

      return () => {
        htmlTableElement?.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleScroll);
      };
    }
  }, []);

  /*
    Preparing the columns for the table

    Note about the useMemo hook:
    Tanstack DataTable relies heavily on the useMemo hook to avoid re-rendering the table on every state change.
    In fact, it doesn't need useMemo, but a stable reference.
    useMemo is used here to ensure that the reference to the columns is stable.
    See https://tanstack.com/table/latest/docs/faq#how-do-i-stop-infinite-rendering-loops

    It might be restrictive to how you would implement your columns. Once they are here, they can't be directly changed
    unless the whole DataTableV2 component is re-rendered. If you need to change the columns dynamically, you should use
    the columnVisibilityState prop to hide/show columns at runtime.

    You can still of course change the content of a cell dynamically by defining the cell functions
    in your column definition.
  */
  const allColumns = React.useMemo(
    () =>
      generateColumnDef({
        tableUniqueId,
        columns,
        enableTableSelection,
        enableMultiselect,
        isExpandable,
        menuItems,
        psbRef,
        renderExpandedContent,
        rowSelectionDisabled,
        rowForcefullySelected,
        getCheckboxTooltip,
        getRowAriaLabelledBy,
        ...(isSubRowsDataTableV2Enabled ? { getSubRows } : {}),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columns],
  );

  const table = useReactTable({
    columns: allColumns,
    data,
    defaultColumn: {
      minSize: 0,
      size: 0,
    },
    enableColumnPinning: enableColumnFreeze,
    enableMultiRowSelection: enableMultiselect,
    enableRowSelection: enableTableSelection,
    enableSorting,
    state: {
      columnPinning: columnPinningState,
      columnVisibility,
      expanded,
      rowSelection,
      sorting,
    },
    getCoreRowModel: getCoreRowModel(),
    autoResetPageIndex: false,
    getRowCanExpand: canRowExpand,
    getExpandedRowModel: getExpandedRowModel(),
    getRowId: (row, index) => (get(row, '_id') as string) || index.toString(),
    onColumnVisibilityChange: setColumnVisibility,
    onExpandedChange: setExpanded,
    onRowSelectionChange: setRowSelection,
    getSortedRowModel: isClientSideSorting ? getSortedRowModel() : undefined,
    manualSorting: enableSorting && serverSideSorting,
    onSortingChange: setSorting,
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: MINIMUM_PAGE_SIZE,
      },
    },
    ...(enablePagination
      ? {
          getPaginationRowModel: getPaginationRowModel(),
        }
      : {}),
    getSubRows: isSubRowsDataTableV2Enabled ? getSubRows : undefined,
  });

  useEffect(() => {
    if (tableRef) {
      tableRef.current = table;
    }
  }, [table, tableRef]);

  // This is used to update the parent component with the selected rows
  useEffect(() => {
    if (onRowSelectionChange) {
      // Use sorted model when client-side sorting is enabled, otherwise fall back to core model
      const rowModel = isClientSideSorting ? table.getSortedRowModel() : table.getCoreRowModel();
      const selectedRows = rowModel.rows.filter(row => row.getIsSelected()).map(row => row.original);
      onRowSelectionChange(selectedRows);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowSelection, table, data.length]);

  // Resets the page index when data changes
  useEffect(() => {
    if (table.getState().pagination.pageIndex > 0) {
      table.resetPageIndex();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.length]);

  // GENERAL LOGIC

  // Retrieves the Cell's Width (CSS Style)
  const getCellWidthStyle = (columnSize: number): Record<string, number | string> => {
    const widthStyle: Record<string, number | string> = {};

    // For the columns with no size definition, assign a "flex: 1" to fill the remaining space
    if (columnSize) {
      widthStyle.width = `${columnSize}px`;
    } else {
      widthStyle.flex = 1;
    }

    return widthStyle;
  };

  // HEADER LOGIC

  // This gets the sort icons for the table header and shows both the sort icons together
  const getSortIcon = (header: Header<T, unknown>, isGrouped: boolean) => {
    if (!enableSorting || isGrouped || !header.column.getCanSort()) {
      return null;
    }

    const columnId = header.id;
    const sortKey = header.column.columnDef.meta?.serverSideSortKey ?? columnId;
    const currentSortingColumn = serverSideSorting && sortedColumns ? sortedColumns.find(s => s.id === sortKey) : sorting.find(s => s.id === columnId);
    const isSortedAsc = currentSortingColumn && !currentSortingColumn.desc;
    const isSortedDesc = currentSortingColumn && currentSortingColumn.desc;

    let sortByLabel = isSortedDesc ? formatMessage('DataTableV2.Sorting.ClickToSortAscending') : formatMessage('DataTableV2.Sorting.ClickToSortDescending');

    if (isSortedAsc) {
      sortByLabel = `${sortByLabel}. ${formatMessage('DataTableV2.Sorting.CurrentlySortedAscending')}`;
    } else if (isSortedDesc) {
      sortByLabel = `${sortByLabel}. ${formatMessage('DataTableV2.Sorting.CurrentlySortedDescending')}`;
    }

    return (
      <div className="sort-icons" aria-label={sortByLabel}>
        <span className={`sort-icon asc ${isSortedAsc ? 'active' : 'inactive'}`} aria-hidden>
          <i className="icon-cheveron-up" />
        </span>
        <span className={`sort-icon desc ${isSortedDesc ? 'active' : 'inactive'}`} aria-hidden>
          <i className="icon-cheveron-down" />
        </span>
      </div>
    );
  };

  // Logic for the column sorting
  const handleSorting = (columnId: string, isDesc: boolean, sortKey?: string) => {
    const sort = { id: columnId, desc: isDesc };

    if (enableSorting && serverSideSorting) {
      sort.id = sortKey || columnId;
      return onServerSideSorting && onServerSideSorting(sort);
    }

    setSorting([sort]);
  };

  /**
   * Handles click and keyboard events for table rows
   * @param row - The row data
   * @param event - Optional keyboard event
   * @param isKeyboardEvent - Flag indicating if the event was triggered by keyboard
   */
  const handleRowClick = (row: Row<T>, event?: React.KeyboardEvent<HTMLTableRowElement | HTMLButtonElement>, isKeyboardEvent = false): void => {
    if (!onRowClick) return;
    if (!isKeyboardEvent || event?.key === ENTER_KEY) {
      onRowClick(row.original);
    }
  };

  // Retrieves all the Header's Cell class names
  const getHeaderCellClassNames = (header: Header<T, unknown>, isFirstAvailableColumn: boolean, isLastFrozen: boolean) => {
    const isGrouped = Array.isArray(header.column.columns) && header.column.columns.length > 0;
    const isPinned = header.column.getIsPinned();
    const metaCustomClass = header.column.columnDef.meta?.customClass ?? '';
    const isRightAligned = header.column.columnDef.meta?.isRightAligned ?? false;

    return classNames({
      frozen: isPinned,
      'frozen-last': isLastFrozen,
      scrolled: isPsbScrolledX,
      'tst-thc': !isGrouped,
      'tst-thg': isGrouped,
      'first-available-colum': isFirstAvailableColumn,
      'right-aligned': isRightAligned,
      [metaCustomClass]: true,
    });
  };

  // Retrieves Header's Width (CSS Style)
  const getHeaderWidthStyle = (header: Header<T, unknown>): Record<string, number | string> => {
    const isGrouped = Array.isArray(header.column.columns) && header.column.columns.length > 0;
    let widthStyle: Record<string, number | string> = {};

    // Another way to get size of single column: header.column.columnDef.size
    if (isGrouped) {
      const widthStyleValue = header.column.columns.reduce((total, item) => total + item.getSize(), 0);
      widthStyle.minWidth = `${widthStyleValue}px`;
    } else {
      widthStyle = getCellWidthStyle(header.column.getSize());
    }

    return widthStyle;
  };

  // Retrieves the Header's Cell Inline Styles
  const getHeaderCellInlineStyles = (header: Header<T, unknown>): Record<string, number | string | undefined> => {
    const isPinned = header.column.getIsPinned();
    const position = isPinned === 'left' ? `${header.getStart()}px` : `${header.column.getAfter()}px`;

    const inlineStyles = {
      left: isPinned === 'left' ? position : undefined,
      right: isPinned === 'right' ? position : undefined,
      ...getHeaderWidthStyle(header),
    };

    return inlineStyles;
  };

  // Retrieves the Header's Cells
  const getHeaderCells = (headerGroup: HeaderGroup<T>): JSX.Element => (
    <>
      {onRowClick && <th key="selectRowHeader" />}
      {headerGroup.headers.map((header, index) => {
        const headerId = header.id;
        const sortKey = header.column.columnDef.meta?.serverSideSortKey ?? headerId;
        const isGrouped = Array.isArray(header.column.columns) && header.column.columns.length > 0;
        const isSortingDesc =
          serverSideSorting && sortedColumns ? !sortedColumns.find(s => s.id === sortKey && s.desc) : !sorting.some(s => s.id === headerId && s.desc);
        const sortingClasses = header.column.getCanSort() ? 'cursor-pointer select-none tst-thc-content' : '';
        const isPinned = header.column.getIsPinned();
        const nextHeader = headerGroup.headers[index + 1];
        const isLastFrozen = isPinned === 'left' && nextHeader?.column.getIsPinned() !== 'left' && hasHorizontalScroll;

        const headerContent = flexRender(header.column.columnDef.header, header.getContext());
        const sortIcon = getSortIcon(header, isGrouped);

        return (
          <th
            className={getHeaderCellClassNames(header, index === 0, isLastFrozen)}
            colSpan={header.colSpan}
            key={headerId}
            style={getHeaderCellInlineStyles(header)}
          >
            {!header.isPlaceholder && (
              <>
                {header.column.getCanSort() ? (
                  <InvisibleButton className={sortingClasses} onClick={() => handleSorting(headerId, isSortingDesc, sortKey)}>
                    {header.column.columnDef.meta?.isRightAligned ? (
                      <>
                        {sortIcon}
                        {headerContent}
                      </>
                    ) : (
                      <>
                        {headerContent}
                        {sortIcon}
                      </>
                    )}
                  </InvisibleButton>
                ) : (
                  <div>{flexRender(header.column.columnDef.header, header.getContext())}</div>
                )}
              </>
            )}
          </th>
        );
      })}
    </>
  );

  // Retrieves the Header's Rows
  const getHeaderRows = (): JSX.Element => (
    <>
      {table.getHeaderGroups().map(headerGroup => (
        <tr key={headerGroup.id} className="tst-thr">
          {getHeaderCells(headerGroup)}
        </tr>
      ))}
    </>
  );

  // BODY

  // Renders the infinite scroll trigger and the spinning loader
  const getInfiniteScrollLoader = (): JSX.Element => {
    const hasData = data.length > 0;
    const hasInfiniteScrollEnabled = infiniteScrollOptions && infiniteScrollOptions?.show;
    const onEnterIsDefined = infiniteScrollOptions?.onEnter !== undefined;
    return (
      <>
        {hasData && hasInfiniteScrollEnabled && onEnterIsDefined && (
          <tr key="loading-spinner" className={infiniteScrollOptions.show ? 'loading-spinner-row show' : 'loading-spinner-row'}>
            <td colSpan={4}>{data && data.length > 0 && <InfiniteScrollComponent options={infiniteScrollOptions} psbRef={psbRef} />}</td>
          </tr>
        )}
      </>
    );
  };

  // Retrieves all the Body's Cell class names
  const getBodyCellClassNames = (cell: Cell<T, unknown>, isFirstAvailableColumn: boolean, isLastFrozen: boolean): string => {
    const customClass = cell.column.columnDef.meta?.customClass ?? '';
    const isPinned = cell.column.getIsPinned();
    const isRightAligned = cell.column.columnDef.meta?.isRightAligned ?? false;

    return classNames({
      frozen: isPinned,
      'frozen-last': isLastFrozen,
      scrolled: isPsbScrolledX,
      'tst-tbc': true,
      'first-available-colum': isFirstAvailableColumn,
      'right-aligned': isRightAligned,
      [customClass]: true,
    });
  };

  // Retrieves all the Body's Row class names
  const getBodyRowClassNames = (row: Row<T>): string => {
    const isSelected = row.getIsSelected();
    const isExpanded = row.getIsExpanded();
    const isClickable = !!onRowClick;
    const additionalClassNames = getAdditionalRowClassNames && getAdditionalRowClassNames(row);
    const hasSubRows = row.subRows.length > 0;
    const isSubRow = row.depth > 0;

    return classNames(
      {
        'active-tbr-hover': tableClassNames.isBodyHoverActive || onRowClick,
        clickable: isClickable,
        expanded: isExpanded,
        'has-sub-rows': hasSubRows,
        selected: isSelected,
        'tst-tbr': true,
        'is-sub-row': isSubRow,
      },
      additionalClassNames,
    );
  };

  // Retrieves the Header's Cells Inline Styles
  const getBodyCellsInlineStyles = (cell: Cell<T, unknown>): Record<string, number | string | undefined> => {
    const isPinned = cell.column.getIsPinned();
    const position = isPinned === 'left' ? `${cell.column.getStart()}px` : `${cell.column.getAfter()}px`;

    const inlineStyles = {
      left: isPinned === 'left' ? position : undefined,
      right: isPinned === 'right' ? position : undefined,
      ...getCellWidthStyle(cell.column.getSize()),
    };

    return inlineStyles;
  };

  // Retrieves the Body's Cells
  const getBodyCells = (row: Row<T>): JSX.Element => (
    <>
      {onRowClick && (
        <td key={`selectRow-${row.id}`}>
          <button
            aria-label={formatMessage('Common.Accessibility.SelectRow')}
            className="tst-tbr-row-action"
            data-testid={`tst-tbr-row-action-${row.id}`}
            onClick={event => {
              event.stopPropagation();
              handleRowClick(row);
            }}
            onKeyDown={event => handleRowClick(row, event, true)}
            tabIndex={0}
            type="button"
          />
        </td>
      )}
      {row.getVisibleCells().map((cell, index) => {
        const isPinned = cell.column.getIsPinned();
        const nextCell = row.getVisibleCells()[index + 1];
        const isLastFrozen = isPinned === 'left' && nextCell?.column.getIsPinned() !== 'left' && hasHorizontalScroll;

        return (
          <td className={getBodyCellClassNames(cell, index === 0, isLastFrozen)} key={cell.id} style={getBodyCellsInlineStyles(cell)}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </td>
        );
      })}
    </>
  );

  const getExpandedContent = (row: Row<T>, index: number): JSX.Element => {
    if (!renderExpandedContent) return <></>;

    return (
      <tr className="expanded-content" id={`expanded-content-${row.index}-${tableUniqueId}`}>
        <td colSpan={allColumns.length}>{renderExpandedContent(row)}</td>
      </tr>
    );
  };

  // Retrieves the Body's Rows
  const getBodyRows = (): JSX.Element => {
    let firstErrorRowFound = false;

    return (
      <>
        {data.length > 0 &&
          table.getRowModel().rows.map((row, index) => {
            const rowClassNames = getBodyRowClassNames(row);
            const isFirstError = rowClassNames.includes('is-first-error');
            let rowRef;

            if (isFirstError && !firstErrorRowFound) {
              rowRef = firstErrorRowRef;
              firstErrorRowFound = true;
            }

            return (
              <React.Fragment key={row.id}>
                <tr ref={rowRef} className={rowClassNames} onClick={() => handleRowClick(row)}>
                  {getBodyCells(row)}
                </tr>
                {row.getIsExpanded() && isExpandable && getExpandedContent(row, index)}
              </React.Fragment>
            );
          })}
        {getInfiniteScrollLoader()}
      </>
    );
  };

  // EMPTY DATA

  // Retrieves the Empty Data Component
  const getEmptyDataComponent = (): JSX.Element => (
    <>
      {data.length === 0 && (
        <div className="data-table-v2-empty-container">
          <NoDataComponent />
        </div>
      )}
    </>
  );

  // Renders the SelectedDataPanel Component if needed
  const getSelectedRowPanel = (): Nullable<JSX.Element> => {
    if (!enableTableSelection || !multiselectActionsOptions) {
      return null;
    }

    return (
      <SelectedDataPanel
        selectedData={table.getSelectedRowModel().rows.map(row => row.original)}
        multiselectActionsOptions={multiselectActionsOptions}
        clearSelection={() => {
          if (onClearSelection) {
            onClearSelection();
          }
          table.toggleAllRowsSelected(false);
        }}
        disabled={disabledActions}
      />
    );
  };

  // TABLE STRUCTURE

  const getTable = (): JSX.Element => {
    const { pageIndex } = table.getState().pagination;
    const hasFirstErrorRow = table.getRowModel().rows.some(row => getBodyRowClassNames(row).includes('is-first-error'));

    return (
      <table
        className={classNames('data-table-v2', `page-index-${pageIndex}`)}
        {...(tableAriaLabel ? { 'aria-label': tableAriaLabel } : {})}
        {...(tableTestId ? { 'data-testid': tableTestId } : {})}
      >
        <thead className={classNames('tst-thead', { 'is-scrolled': isPsbScrolledY })}>{getHeaderRows()}</thead>
        <tbody className={classNames('tst-tbody', { 'has-no-error': !hasFirstErrorRow })}> {getBodyRows()}</tbody>
      </table>
    );
  };

  const getPagination = (): JSX.Element => {
    const { pageIndex, pageSize } = table.getState().pagination;

    return (
      <div className={classNames('data-table-pagination', { 'is-scrolled': isPsbScrolledY })}>
        <div className="table-info-section">
          {!hidePaginationTotal && (tableTotalText || formatMessage('Common.TotalOf', { total: data.length }))}
          {tableBottomAction && data.length > 0 && (
            <SecondaryLink
              className="secondary-link-v2"
              onClick={tableBottomAction.onClick}
              aria-label={tableBottomAction.ariaLabel || tableBottomAction.label}
            >
              {tableBottomAction.label}
            </SecondaryLink>
          )}
        </div>
        <div className="pagination-container">
          <Pagination numberOfPages={table.getPageCount()} activePage={pageIndex + 1} onClick={page => table.setPageIndex(page - 1)} />
          <PaginationPageSizeDropdown pageSize={pageSize} setPageSize={table.setPageSize} />
        </div>
      </div>
    );
  };

  // RENDER

  return (
    <>
      {getSelectedRowPanel()}
      <PerfectScrollbar
        className={classNames('data-table-v2-psb-container', { 'is-paginated': enablePagination })}
        ref={psbRef}
        options={{ suppressScrollX: perfectScrollBarOptions.suppressScrollX, suppressScrollY: perfectScrollBarOptions.suppressScrollY }}
      >
        {data.length > 0 ? getTable() : getEmptyDataComponent()}
      </PerfectScrollbar>
      {isPsbScrolledX && <div className="frozen-v-shadow" />}
      {isPsbScrolledY && <div className="frozen-h-shadow" />}

      {enablePagination && !!data.length && getPagination()}
    </>
  );
}

const DefaultNoDataComponent: React.FC = () => (
  <div className="tst-default-empty-table">
    <FormattedMessage id="EmptyState.EmptyData" />
  </div>
);

const DefaultInfiniteScrollComponent: React.FC<{ options: InfiniteScrollOptions | undefined; psbRef: React.MutableRefObject<PerfectScrollbar | null> }> = ({
  options,
  psbRef,
}) => {
  const currentPsb = (psbRef.current as any)?._container as HTMLElement;
  const scrollableContainer = options?.scrollAncestor ? options.scrollAncestor : currentPsb;

  // Use a ref to track if this is the first mount to avoid remounting unnecessarily
  const containerRef = useRef(scrollableContainer);

  // Update the ref if the container changes
  useEffect(() => {
    containerRef.current = scrollableContainer;
  }, [scrollableContainer]);

  return (
    <>
      {options && options.show && (
        <div className="load-more d-flex justify-content-center" data-testid="waypoint-loading-spinner">
          {scrollableContainer && <Waypoint key={options.waypointKey} onEnter={options?.onEnter} scrollableAncestor={scrollableContainer} />}
          <LoadingSpinner />
        </div>
      )}
    </>
  );
};

export default DataTableV2;
